import { PageOptions } from '@graphcommerce/framer-next-pages'
import { CustomerDocument, useCustomerQuery } from '@graphcommerce/magento-customer'
import { PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { LayoutNavigation, LayoutNavigationProps, LayoutOverlayProps } from '../../components'
import { AccountPageTabs } from '../../components/Account/AccountPageTabs'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { AccountDashboardOrdersDocument } from '../../graphql/AccountDashboardOrders.gql'
import useCallAPI from '../../hooks/useCallAPI'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'

type GetPageStaticProps = GetStaticProps<LayoutOverlayProps>

function AccountPage() {
  const { query } = useRouter()

  const orders = useCustomerQuery(AccountDashboardOrdersDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pageSize: 5,
      currentPage: Number(query?.page ?? 1),
      search: query?.search?.toString() || undefined,
      sort: { sort_direction: 'DESC', sort_field: 'CREATED_AT' },
    },
  })
  const { data } = orders
  const customer = data?.customer
  const [acessToken, setAccessToken] = useState<string>()
  const [reviews, setReviews] = useState<any[]>()

  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const { apiCall: authAPICall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/oauth/token?client_id=${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}&client_secret=${process.env.NEXT_PUBLIC_YOTPO_SECRET_KEY}&grant_type=client_credentials`,
    contentType: 'application/json',
    method: 'GET',
  })

  const { apiCall: getAllReviewsCall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/v1/apps/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/reviews?utoken=${acessToken}&count=500`,
    contentType: 'application/json',
    method: 'GET',
  })

  const router = useRouter()
  const { navigateTo } = router.query

  useEffect(() => {
    const fetchAuthToken = async () => {
      const authRes = await authAPICall()
      if (authRes && authRes.status === 200 && authRes?.data?.access_token) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setAccessToken(authRes?.data?.access_token)
      }
    }

    fetchAuthToken().catch((e) => {
      // addtional error handling here, the error must be set by the useCallAPI hook already
      console.error(e)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchAllReviews = async () => {
      const res = await getAllReviewsCall()
      if (res && res.status === 200 && res?.data?.reviews) {
        const userReviews = res?.data?.reviews?.filter(
          (item) =>
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            item?.email?.toUpperCase() === dashboard.data?.customer?.email?.toUpperCase() &&
            item?.archived === false &&
            item?.deleted === false,
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setReviews(userReviews)
      }
    }

    if (acessToken && acessToken.length > 0) {
      fetchAllReviews().catch((e) => {
        // addtional error handling here, the error must be set by the useCallAPI hook already
        console.error(e)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acessToken])

  return (
    <div className='m-auto lg:max-w-maxContentWidth 2xl:px-0'>
      <PageMeta title={i18n._(/* i18n */ 'Orders')} metaRobots={['noindex']} />
      <div className='w-full'>
        <AccountPageTabs defaultTabIndex={navigateTo?.toString()} />
      </div>
    </div>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}
AccountPage.pageOptions = pageOptions

export default AccountPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const layout = staticClient.query({ query: LayoutDocument })

  return {
    props: {
      apolloState: await conf.then(() => client.cache.extract()),
      ...(await layout).data,
    },
  }
}
